import React, { ReactElement } from 'react';

import { History } from 'history';

import { BUTTON_SIZES, BUTTON_TYPES, EvButton, EvTableColumn } from '@evinced-private/ui-common';

import { navigateToBusinessUnit } from 'src/apps/dashboard/helpers/NavigationHelpers';
import { IBusinessUnitRow } from 'src/apps/dashboard/types/BusinessUnitsTableTypes';
import { Logger } from 'src/common/services/Logger';

import { ScoreCellFormatter } from './ScoreCellFormatter';

const formatter = new Intl.NumberFormat('en-US');

const renderLocalizedNumberCellFormatter = (value: number): ReactElement => {
	return <div className="number-cell-formatter">{formatter.format(value)}</div>;
};

// Memoize column definitions to prevent re-creation on every render
const createBusinessUnitsColumns = (
	history: History,
	showUniqueIssuesColumn: boolean,
	basePath?: string
): EvTableColumn[] => {
	const columns: EvTableColumn[] = [
		{
			dataField: 'name',
			text: 'Name',
			style: { width: '200px' },
			headerStyle: { width: '200px' },
			sort: true
		},
		{
			dataField: 'criticalIssues',
			text: 'Critical Issues',
			style: { width: '120px' },
			headerStyle: { width: '120px' },
			sort: true,
			formatter: renderLocalizedNumberCellFormatter
		},
		{
			dataField: 'seriousIssues',
			text: 'Serious Issues',
			style: { width: '120px' },
			headerStyle: { width: '120px' },
			sort: true,
			formatter: renderLocalizedNumberCellFormatter
		}
	];

	if (showUniqueIssuesColumn) {
		columns.push({
			dataField: 'uniqueIssues',
			text: 'Unique Issues',
			style: { width: '120px' },
			headerStyle: { width: '120px' },
			sort: true,
			formatter: renderLocalizedNumberCellFormatter
		});
	}

	columns.push(
		{
			dataField: 'scoreValue',
			text: 'Score',
			style: { width: '150px' },
			headerStyle: { width: '150px' },
			sort: true,
			formatter: (value: number, row: IBusinessUnitRow) => (
				<ScoreCellFormatter scoreData={row?.score} />
			)
		},
		{
			dataField: 'actions',
			text: 'Actions',
			style: { width: '120px' },
			headerStyle: { width: '120px' },
			formatter: (cell: unknown, row: IBusinessUnitRow) =>
				actionFormatter(cell, row, history, basePath)
		}
	);

	return columns;
};

export const getBusinessUnitsColumns = (
	history: History,
	showUniqueIssuesColumn = true,
	basePath?: string
): EvTableColumn[] => {
	return createBusinessUnitsColumns(history, showUniqueIssuesColumn, basePath);
};

const actionFormatter = (
	_cell: unknown,
	row: IBusinessUnitRow,
	history: History,
	basePath?: string
): ReactElement => {
	// Disable button for "Unspecified" business unit
	const isUnspecified = row.id === 'unspecified';

	return (
		<EvButton
			onClick={() => handleViewDetails(row.id, history, basePath)}
			ariaLabel={
				isUnspecified
					? 'No details available for unspecified issues'
					: `View details for ${row.name}`
			}
			type={BUTTON_TYPES.GHOST}
			size={BUTTON_SIZES.SMALL}
			disabled={isUnspecified}
		>
			View Details
		</EvButton>
	);
};

const handleViewDetails = (unitId: string, history: History, basePath?: string): void => {
	if (unitId === 'unspecified') {
		Logger.info('Cannot navigate to unspecified business unit');
		return;
	}

	Logger.info('Navigating to business unit:', unitId);
	navigateToBusinessUnit(history, unitId, basePath);
};
